import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ITitleAssetUrlDTO } from '../../../../api/assets.dao';
import AssetsViewModel from './assets.viewmodel';

export const droppableAsset = 'droppable_asset';
const droppableTag = 'tr';
const droppableZoneIsImage = 'true';

interface IUseAssetsDragAndDropResult {
    draggingFile: ITitleAssetUrlDTO;
    currentDroppableElementId: number;
    isDraggingOverPage: boolean;
    handleDragStart: (e: React.DragEvent<HTMLElement>, file: ITitleAssetUrlDTO) => void;
    handleDragEnd: () => void;
}

export const useAssetsDragAndDrop = (vm: AssetsViewModel): IUseAssetsDragAndDropResult => {
    const [draggingFile, setDraggingFile] = useState<ITitleAssetUrlDTO>(null);
    const [currentDroppableElementId, setCurrentDroppableElementId] = useState<number>(null);
    const [isDraggingOverPage, setIsDraggingOverPage] = useState<boolean>(false);
    const dragCounter = useRef(0);

    const resetDragAndDropValues = useCallback((): void => {
        setDraggingFile(null);
        setCurrentDroppableElementId(null);
    }, []);

    useEffect(() => {
        const getDroppableZone = (e: DragEvent): HTMLElement =>
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
            document.elementFromPoint(e.clientX, e.clientY)?.closest('.assets-list-item') as HTMLElement;

        const isDroppableZone = (el: HTMLElement): boolean =>
            el?.dataset?.default === droppableAsset && el.tagName.toLowerCase() === droppableTag;

        const isDraggingFiles = (e: DragEvent): boolean =>
            Array.from(e.dataTransfer.types).includes('Files');

        const uploadFromUncategorized = (file: ITitleAssetUrlDTO, organizationId: string, attendeeId: string): void => {
            const response = vm.uploadProfileImageFromUncategorized(file, organizationId, attendeeId);
            response
                .then(data => { if (data === false) resetDragAndDropValues(); })
                .catch(() => resetDragAndDropValues());
        };

        const onDragOver = (e: DragEvent): void => {
            e.preventDefault();
            try {
                const droppableBelow = getDroppableZone(e);
                const isOverDroppableZone = droppableBelow && isDroppableZone(droppableBelow);
                setCurrentDroppableElementId(
                    isOverDroppableZone ? (Number(droppableBelow.dataset.value) ?? null) : null
                );
            // eslint-disable-next-line no-empty
            } catch (error) {}
        };

        const onDragEnter = (e: DragEvent): void => {
            if (!isDraggingFiles(e)) return;
            dragCounter.current++;
            setIsDraggingOverPage(true);
        };

        const onDragLeave = (e: DragEvent): void => {
            if (!isDraggingFiles(e)) return;
            dragCounter.current--;
            if (dragCounter.current === 0) setIsDraggingOverPage(false);
        };

        const onDropResetState = (): void => {
            dragCounter.current = 0;
            setIsDraggingOverPage(false);
            setCurrentDroppableElementId(null);
        };

        const onDrop = (e: DragEvent): void => {
            e.preventDefault();

            const isOsFileDrop = e.dataTransfer.files?.length > 0 && !e.dataTransfer.getData('text');
            if (isOsFileDrop) {
                vm.uploadAssets(Array.from(e.dataTransfer.files));
                return;
            }

            try {
                const droppedFile = JSON.parse(e.dataTransfer.getData('text'));
                const droppableBelow = getDroppableZone(e);
                if (isDroppableZone(droppableBelow)) {
                    const isImageSlot = droppableBelow.dataset.image === droppableZoneIsImage;
                    if (isImageSlot) {
                        vm.checkIsFileReplacing(droppableBelow.dataset.organization, droppableBelow.dataset.attendee, () => {
                            uploadFromUncategorized(droppedFile, droppableBelow.dataset.organization, droppableBelow.dataset.attendee);
                        });
                    } else {
                        uploadFromUncategorized(droppedFile, droppableBelow.dataset.organization, droppableBelow.dataset.attendee);
                    }
                }
            // eslint-disable-next-line no-empty, @typescript-eslint/no-shadow
            } catch (e) {}
        };

        document.addEventListener('dragover', onDragOver);
        document.addEventListener('dragenter', onDragEnter);
        document.addEventListener('dragleave', onDragLeave);
        document.addEventListener('drop', onDropResetState, true);
        document.addEventListener('drop', onDrop);

        return () => {
            document.removeEventListener('dragover', onDragOver);
            document.removeEventListener('dragenter', onDragEnter);
            document.removeEventListener('dragleave', onDragLeave);
            document.removeEventListener('drop', onDropResetState, true);
            document.removeEventListener('drop', onDrop);
        };
    }, []);

    const handleDragStart = useCallback((e: React.DragEvent<HTMLElement>, file: ITitleAssetUrlDTO): void => {
        setDraggingFile(file);
        e.dataTransfer.setData('text', JSON.stringify(file));
    }, []);

    const handleDragEnd = resetDragAndDropValues;

    return {
        draggingFile: draggingFile,
        currentDroppableElementId: currentDroppableElementId,
        isDraggingOverPage: isDraggingOverPage,
        handleDragStart: handleDragStart,
        handleDragEnd: handleDragEnd
    };
};